import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
} from "reactstrap";
// core components
import { useCallback, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import Loading from "components/Loading";
import PipelineOverviewSummary from "components/Views/PipelineOverview/Summary";
import PipelineOverviewFiltersPreview from "components/Views/PipelineOverview/FiltersPreview";
import PipelineOverviewTranscription from "components/Views/PipelineOverview/Transcription";
import API from "service/api";
import PipelineOverviewCollection from "components/Views/PipelineOverview/Collection";
import PipelineOverviewMatching from "components/Views/PipelineOverview/Matching";
import PipelineOverviewGenerative from "components/Views/PipelineOverview/Generative";
import PipelineOverviewDone from "components/Views/PipelineOverview/Done";
import PipelineStatusType from "enum/pipeline-status";

const PipelineOverview = () => {
  const { id: pipelineId } = useParams();
  const location = useLocation();
  const executionId = new URLSearchParams(location.search).get("executionId");
  const [loading, setLoading] = useState(false);
  const [pipelineExecuted, setPipelineExecuted] = useState(false);
  const [pipelineSummary, setPipelineSummary] = useState({});
  const [pipelineExecution, setPipelineExecution] = useState({});
  const [finishedEventsAmount, setFinishedEventsAmount] = useState(0);
  const [budgetBlocked, setBudgetBlocked] = useState(false);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [budgetStatus, setBudgetStatus] = useState({});

  const handleBudgetStatusChange = (budgetStatusData) => {
    setBudgetBlocked(budgetStatusData.blocked);
    setBudgetLoading(budgetStatusData.loading);
    setBudgetStatus(budgetStatusData);
  };

  const execute = useCallback(() => {
    if (budgetBlocked) {
      toast.error("Não é possível executar. Orçamento esgotado.");
      return;
    }

    setLoading(true);
    setPipelineExecuted(true);

    API.post("/pipeline/execute", {
      pipelineId,
    })
      .then((response) => {
        toast.success("Pipeline executada com sucesso.");
        setLoading(false);
        setPipelineExecution(response.data);
      })
      .catch(() => {
        toast.error("Erro ao executar pipeline!");
        setPipelineExecution({ status: PipelineStatusType.ERROR });
        setPipelineExecuted(false);
        setLoading(false);
        return;
      });
  }, [budgetBlocked]);

  const renderBudgetAlert = () => {
    if (!pipelineSummary?.client_id || pipelineSummary.status !== "IDLE")
      return null;

    if (budgetStatus.loading) {
      return (
        <div className="alert alert-info d-flex align-items-center mb-3">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="sr-only">Verificando orçamento...</span>
          </div>
          Verificando orçamento disponível...
        </div>
      );
    }

    if (budgetStatus.error) {
      return (
        <div className="alert alert-warning mb-3">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {budgetStatus.error}
        </div>
      );
    }

    if (budgetStatus.blocked) {
      return (
        <div className="alert alert-danger mb-3">
          <i className="fas fa-times-circle me-2"></i>
          <strong>Orçamento Esgotado </strong>
          <br />
          Este cliente atingiu 100% do orçamento disponível. Não é possível
          executar pipeline.
        </div>
      );
    }

    if (
      budgetStatus.alertThreshold &&
      budgetStatus.percentage >= budgetStatus.alertThreshold
    ) {
      const alertLevel =
        budgetStatus.percentage >= 90
          ? "danger"
          : budgetStatus.percentage >= 70
            ? "warning"
            : "info";

      return (
        <div className={`alert alert-${alertLevel} mb-3`}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Atenção - Orçamento </strong>
          <br />
          Este cliente já utilizou {budgetStatus.percentage}% do orçamento
          disponível.
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {/* Page content */}
      <Container className="mt-7" fluid>
        <Col className="" xl="12">
          <Card className="bg-secondary shadow">
            <CardHeader className="bg-white border-0">
              <Row className="align-items-center">
                <Col xs="8">
                  <h3 className="mb-0">Execução da Pipeline: {pipelineId} </h3>
                </Col>
                <Col className="text-right" xs="4"></Col>
              </Row>
            </CardHeader>
            <CardBody>
              {renderBudgetAlert()}
              <Row className="mb-3">
                <Col className="col-12">
                  <div className="d-flex">
                    <Button
                      color="primary"
                      style={{ fontSize: "0.7em" }}
                      disabled={
                        pipelineExecuted || budgetBlocked || budgetLoading
                      }
                      onClick={execute}
                      title={
                        budgetBlocked
                          ? "Orçamento esgotado"
                          : budgetLoading
                            ? "Verificando orçamento..."
                            : pipelineExecuted
                              ? "Pipeline já executada"
                              : "Executar pipeline"
                      }
                    >
                      {budgetLoading
                        ? "Verificando orçamento..."
                        : !pipelineExecuted
                          ? "Executar pipeline"
                          : "Pipeline executada"}
                    </Button>
                    <Button
                      color="secondary"
                      onClick={() => window.location.reload()}
                      style={{ fontSize: "0.7em" }}
                    >
                      Atualizar informações <i className="fa fa-sync"></i>
                    </Button>
                  </div>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col className="col-12">
                  <PipelineOverviewSummary
                    setPipelineExecuted={setPipelineExecuted}
                    pipelineExecution={pipelineExecution}
                    setPipelineSummary={setPipelineSummary}
                    onBudgetStatusChange={handleBudgetStatusChange}
                    executionId={executionId}
                  />
                </Col>
              </Row>
              {pipelineSummary?.metadata_is_filter && (
                <Row className="mb-2">
                  <Col className="col-12">
                    <PipelineOverviewFiltersPreview executionId={executionId} />
                  </Col>
                </Row>
              )}
              <Row className="mb-2">
                <Col className="col-12">
                  <PipelineOverviewCollection
                    sources={pipelineSummary?.storage_sources || []}
                    executionId={executionId}
                  />
                </Col>
              </Row>
              {pipelineSummary?.storage_sources?.some(
                (source) => source.type === "AUDIO",
              ) && (
                <Row className="mb-2">
                  <Col className="col-12">
                    <PipelineOverviewTranscription executionId={executionId} />
                  </Col>
                </Row>
              )}
              {pipelineSummary?.libraries &&
                pipelineSummary?.libraries.length > 0 && (
                  <Row className="mb-2">
                    <Col className="col-12">
                      <PipelineOverviewMatching
                        setFinishedEventsAmount={setFinishedEventsAmount}
                        executionId={executionId}
                      />
                    </Col>
                  </Row>
                )}
              {pipelineSummary?.prompts &&
                pipelineSummary?.prompts.length > 0 && (
                  <Row className="mb-2">
                    <Col className="col-12">
                      <PipelineOverviewGenerative
                        setFinishedEventsAmount={setFinishedEventsAmount}
                        executionId={executionId}
                      />
                    </Col>
                  </Row>
                )}
              <Row className="mb-2">
                <Col className="col-12">
                  <PipelineOverviewDone amount={finishedEventsAmount} />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Container>
    </>
  );
};

export default PipelineOverview;
